import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, DestroyRef, inject } from '@angular/core';
import { VendorService } from 'cbd-proxies';
import {
  BaseHierarchicalCrudComponent,
  ControlTypes,
  FieldMapperHelper,
  GenericForm,
  GenericFormField,
  ICrudService,
  InputTypes,
  ZnHierarchicalViewComponent,
} from '@zendra/shared';
import { WoodCertificationDetailService } from 'geographic/proxy';
import { BioForestAbpService } from 'orchestrator/proxy';
import { WoodCertificationDetailPlanningOrchestratorService } from 'projects/orchestrator/src/lib/proxy/orchestrator/controllers/wood-certification-detail-planning-orchestrator.service';
import { CERTIFIEDCONTRACTS_CONFIG, CERTIFIEDCONTRACTS_FIELDS } from './certified-contracts.config';
import { ConfirmationService } from '@abp/ng.theme.shared';
import { ListService } from '@abp/ng.core';
import { WoodCertificationDetailOrchestratorPlanningContractCreateDto, WoodCertificationDetailOrchestratorPlanningContractUpdateDto } from 'projects/orchestrator/src/lib/proxy/orchestrator/dtos/wood-certification-detail/models';
import { GetWoodCertificationDetailProjectionListInputDto, WoodCertificationDetailProjectionDto, WoodCertificationDetailProjectionExcelDownloadDto } from 'projects/orchestrator/src/lib/proxy/upm/zendra/cms/projection/wood-certification-details/models';
import { catchError, distinctUntilChanged, map, of, startWith, Subscription, switchMap } from 'rxjs';

@Component({
  selector: 'lib-certified-contracts',
  imports: [
      GenericForm,
      ZnHierarchicalViewComponent,
    ],
  providers: [ListService, WoodCertificationDetailService, ConfirmationService],
  templateUrl: './certified-contracts.html'
})
export class CertifiedContracts extends BaseHierarchicalCrudComponent<
  WoodCertificationDetailProjectionDto,
  WoodCertificationDetailOrchestratorPlanningContractCreateDto,
  WoodCertificationDetailOrchestratorPlanningContractUpdateDto,
  WoodCertificationDetailProjectionExcelDownloadDto
> {
  private readonly destroyRef = inject(DestroyRef);
  private readonly geographicService = inject(WoodCertificationDetailService);
  private readonly orchestratorService = inject(
    WoodCertificationDetailPlanningOrchestratorService,
  );
  private readonly bioForestAbpService = inject(BioForestAbpService);
  private readonly vendorService = inject(VendorService);

  private contractorSyncSubscription?: Subscription;

  service: ICrudService<
    WoodCertificationDetailProjectionDto,
    WoodCertificationDetailOrchestratorPlanningContractCreateDto,
    WoodCertificationDetailOrchestratorPlanningContractUpdateDto,
    WoodCertificationDetailProjectionExcelDownloadDto
  > = {
    create: input => this.orchestratorService.createCertificationDetailForContractByInput(input),
    update: (id, input) => this.orchestratorService.updateCertificationDetailForContractByIdAndInput(id, input),
    projectionConsistency: this.createProjectionConsistency(
      this.orchestratorService,
      'woodCertificationDetailId',
    ),
    deleteByIds: ids => this.geographicService.deleteByIds(ids),
    getList: (query: GetWoodCertificationDetailProjectionListInputDto) =>
      this.orchestratorService.getList(this.withCertificationContext(query)),
    getDownloadToken: () => this.orchestratorService.getDownloadToken(),
    getListAsExcelFile: input => this.orchestratorService.getListAsExcelFile(this.withCertificationContext(input)),
  };
  config = CERTIFIEDCONTRACTS_CONFIG;
  fields = CERTIFIEDCONTRACTS_FIELDS;

  private withCertificationContext<T extends GetWoodCertificationDetailProjectionListInputDto>(input: T): T {
    let woodCertificationId = input.woodCertificationId;
    if (!woodCertificationId?.length) {
      woodCertificationId = this.parentId !== undefined ? [this.parentId] : undefined;
    }
    return {
      ...input,
      woodCertificationId,
      hasContract: true,
    };
  }

  override create(): void {
    if (this.parentId !== undefined && this.parentFilterField) {
      this.selectedEntity = {
        [this.parentFilterField]: this.parentId,
      } as WoodCertificationDetailProjectionDto;
    } else {
      this.selectedEntity = {} as WoodCertificationDetailProjectionDto;
    }

    this.openForm(this.selectedEntity, 'create');
  }

  override edit(row: WoodCertificationDetailProjectionDto): void {
    this.selectedEntity = { ...row };
    this.openForm(row, 'edit');
  }

  protected override prepareCreateDto(
    data: any,
  ): WoodCertificationDetailOrchestratorPlanningContractCreateDto {
    return {
      woodCertificationId: data.woodCertificationId,
      lastCheckDate: null,
      addedDate: data.addedDate ?? null,
      contractId: data.contractId ?? null,
    };
  }

  protected prepareUpdateDto(
    data: any,
    selected: WoodCertificationDetailProjectionDto
  ): WoodCertificationDetailOrchestratorPlanningContractUpdateDto {
    return {
      woodCertificationId: data.woodCertificationId,
      lastCheckDate: selected.lastCheckDate ?? null,
      addedDate: data.addedDate ?? null,
      contractId: data.contractId ?? null,
      concurrencyStamp: selected.concurrencyStamp,
    };
  }

  protected getEntityName(): string {
    return 'CertifiedContracts';
  }

  private openForm(
    row: Partial<WoodCertificationDetailProjectionDto>,
    mode: 'create' | 'edit',
  ): void {
    const fields = this.buildFormFields(row);
    this.genericForm.open({
      title: `${this.localizationResource.LanguageService}::${
        mode === 'create' ? 'TableNewRegistry' : 'EditRecord'
      }`,
      fields,
      entityLocaleKey: this.config.localizationKey,
      mode,
    });

    this.setupContractorFieldSync(row.vendorName ?? null);
  }

  private buildFormFields(
    row: Partial<WoodCertificationDetailProjectionDto>,
  ): GenericFormField[] {
    const fields = FieldMapperHelper.mapFieldsForForm(
      this.fields,
      row,
      this.injector,
    );
    const vendorNameField = this.createVendorNameField(row.vendorName ?? null);
    const contractIndex = fields.findIndex(field => field.key === 'contractId');

    if (contractIndex === -1) {
      return [...fields, vendorNameField];
    }

    return [
      ...fields.slice(0, contractIndex + 1),
      vendorNameField,
      ...fields.slice(contractIndex + 1),
    ];
  }

  private createVendorNameField(vendorName: string | null): GenericFormField {
    return new GenericFormField({
      key: 'vendorName',
      label: 'Geographic::Entity.Vendor',
      controlType: ControlTypes.Textbox,
      type: InputTypes.Text,
      value: vendorName ?? '',
    });
  }

  private setupContractorFieldSync(initialVendorName: string | null): void {
    this.contractorSyncSubscription?.unsubscribe();

    const form = this.genericForm.form();
    const contractControl = form.get('contractId');
    const vendorNameControl = form.get('vendorName');

    if (!contractControl || !vendorNameControl) {
      return;
    }

    vendorNameControl.disable({ emitEvent: false });
    vendorNameControl.setValue(initialVendorName ?? '', { emitEvent: false });

    this.contractorSyncSubscription = contractControl.valueChanges
      .pipe(
        startWith(contractControl.value),
        distinctUntilChanged(),
        switchMap(contractId => this.resolveVendorName(contractId, initialVendorName)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(vendorName => {
        vendorNameControl.setValue(vendorName, { emitEvent: false });
      });
  }

  private resolveVendorName(
    contractId: unknown,
    initialVendorName: string | null,
  ) {
    const parsedContractId = this.toNullableNumber(contractId);

    if (!parsedContractId) {
      return of('');
    }

    if (
      this.toNullableNumber(this.selectedEntity?.contractId) === parsedContractId &&
      initialVendorName
    ) {
      return of(initialVendorName);
    }

    return this.bioForestAbpService.getContractDetail(parsedContractId, undefined).pipe(
      switchMap(contractDetail => {
        const vendorId = this.toNullableNumber(contractDetail?.vendorId);
        if (!vendorId) {
          return of('');
        }

        return this.vendorService.get(vendorId).pipe(
          map(vendor => vendor?.fantasyName ?? ''),
          catchError(() => of('')),
        );
      }),
      catchError(() => of('')),
    );
  }

  private toNullableNumber(value: unknown): number | null {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    if (typeof value !== 'string') {
      return null;
    }

    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return null;
    }

    const parsedValue = Number(trimmedValue);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }
}
